import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import AccountCard, { MockupStateLinks } from '../components/AccountCard';
import { primaryButtonSx } from '../publicStyles';

const BASE = '/mockup/public/reset-password';

// Proposal for /reset_password?code=…, the page the reset email links to. Opening the link only
// checks it; the password changes when the person saves a new one. Links work once and expire.
export default function PublicResetLinkPage() {
    const history = useHistory();
    const location = useLocation();
    const failed = new URLSearchParams(location.search).get('state') === 'failed';
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [mismatch, setMismatch] = React.useState(false);
    const confirmRef = React.useRef<HTMLInputElement>(null);
    const controls = <MockupStateLinks current={failed ? `${BASE}?state=failed` : BASE} options={[
        { label: 'Choose a new password', to: BASE },
        { label: 'Link not valid or expired', to: `${BASE}?state=failed` },
        { label: 'After saving (login notice)', to: '/mockup/public/login?state=passwordChanged' },
    ]} />;

    if (failed) {
        return (
            <AccountCard title="This reset link isn’t valid" controls={controls}
                intro="Reset links work once and expire after an hour. Make sure you opened the whole link from your most recent reset email, or request a new one.">
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button component={RouterLink} to="/mockup/public/forgot-password" sx={{ ...primaryButtonSx, minHeight: 48 }}>Request a new link</Button>
                    <Button component={RouterLink} to="/mockup/public/login" variant="text">Back to log in</Button>
                </Stack>
            </AccountCard>
        );
    }

    const adornment = (
        <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(value => !value)} edge="end"
                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'} aria-pressed={showPassword}>
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <AccountCard title="Choose a new password" controls={controls}
            intro="Enter a new password for your VerbaSchola account. You'll use it the next time you log in.">
            <Stack component="form" spacing={2} sx={{ mt: 3 }} onSubmit={(event: React.FormEvent) => {
                event.preventDefault();
                if (password !== confirmPassword) {
                    setMismatch(true);
                    confirmRef.current?.focus();
                    return;
                }
                history.push('/mockup/public/login?state=passwordChanged');
            }}>
                <TextField label="New password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" autoFocus required fullWidth
                    value={password} onChange={event => { setPassword(event.target.value); setMismatch(false); }}
                    InputProps={{ endAdornment: adornment }} />
                <TextField label="Confirm new password" name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setMismatch(false); }}
                    error={mismatch} helperText={mismatch ? 'The passwords don’t match. Enter the same password twice.' : undefined} inputRef={confirmRef} />
                <Button type="submit" sx={{ ...primaryButtonSx, minHeight: 48 }}>Save new password</Button>
            </Stack>
        </AccountCard>
    );
}
